import { Card } from 'antd';
import { memo, useState } from 'react';
import { renderMoney, renderNumGrouped, renderOptional, renderOptionalPercent } from '../../utils/string.utils';
import { colorRed, colorGreen } from '../../utils/color.utils';
import { Position } from '../widgets/positions/PositionsWidget';
import css from './PositionCard.module.less';
import cn from 'classnames';

interface PositionCardProps {
	position: Position;
	/**
	 * The fund's base currency — what the `…Base` amounts are denominated in.
	 * Passed down rather than read from ProductContext: ui-kit components take
	 * their data through props. See PositionsWidget.
	 */
	fundCurrency?: string;
}

export const PositionCard = memo<PositionCardProps>(({ position, fundCurrency }) => {
	const [expanded, setExpanded] = useState(false);
	const qtyColor = position.quantity ? (position.quantity > 0 ? colorGreen : colorRed) : undefined;
	const sideLower = position.side?.toLowerCase();
	const sideColor = sideLower === 'short' ? colorRed : sideLower === 'long' ? colorGreen : undefined;
	const pnlColor = position.profitLossBase ? (position.profitLossBase > 0 ? colorGreen : colorRed) : undefined;
	return (
		<Card className={css.card} onClick={() => setExpanded(x => !x)}>
			<div className={css.row1}>
				<span className={css.ticker}>{position.productName}</span>
				<div className={css.metrics}>
					<span className={css.qty} style={{ color: qtyColor }}>
						{renderNumGrouped(position.quantity)}
					</span>
					<span className={css.value}>{renderMoney(position.exposureBase, fundCurrency)}</span>
				</div>
			</div>
			<div className={cn(css.props, expanded && css.props_expand)}>
				<div className={css.propsInner}>
					<div className={css.prop}>
						<span className={css.label}>Instrument:</span>
						{position.instrument}
					</div>
					<div className={css.prop}>
						<span className={css.label}>Side:</span>
						<span style={{ color: sideColor }}>{position.side}</span>
					</div>
					<div className={css.prop}>
						<span className={css.label}>Currency:</span>
						{position.currency}
					</div>
					<div className={css.prop}>
						<span className={css.label}>Open Price:</span>
						{renderMoney(position.openPrice, position.currency ?? undefined)}
					</div>
					<div className={css.prop}>
						<span className={css.label}>Last Price:</span>
						{renderMoney(position.lastPrice, position.currency ?? undefined)}
					</div>
					<div className={css.prop}>
						<span className={css.label}>Cost WAVG:</span>
						{renderMoney(position.costWavg, position.currency ?? undefined)}
					</div>
					<div className={css.prop}>
						<span className={css.label}>P&amp;L:</span>
						<span style={{ color: pnlColor }}>{renderMoney(position.profitLossBase, fundCurrency)}</span>
					</div>
					<div className={css.prop}>
						<span className={css.label}>Exposure % of NAV:</span>
						{renderOptionalPercent(position.exposurePctOfNav)}
					</div>
					<div className={css.prop}>
						<span className={css.label}>Alert:</span>
						{renderOptional(position.alert)}
					</div>
					<div className={css.prop}>
						<span className={css.label}>Country:</span>
						{position.country}
					</div>
				</div>
			</div>
		</Card>
	);
});
